import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import useLive from "../hooks/useLive";
import { isListedTicker } from "../data/mockData";

const QUOTE_FILTERS = ["ALL", "USDG", "ETH", "NVDA", "SPY"];
const VENUE_FILTERS = ["ALL", "v2", "v3", "v4"];

function DeltaText({ d }) {
  if (d == null) return <span style={{ color: "var(--ink-muted)" }}>—</span>;
  const cls = d > 0 ? "up" : d < 0 ? "down" : "";
  const sign = d > 0 ? "+" : d < 0 ? "−" : "";
  return <span className={cls}>{sign}{Math.abs(d).toFixed(2)}%</span>;
}

export default function Pairs() {
  const { pairs } = useLive();
  const [q, setQ] = useState("ALL");
  const [v, setV] = useState("ALL");
  const [search, setSearch] = useState("");

  const rows = useMemo(() => {
    const s = search.trim().toUpperCase();
    return pairs.filter((p) => {
      const quoteOK = q === "ALL" || p.pair.split("/")[1] === (q === "ETH" ? "WETH" : q) || p.pair.split("/")[1] === q;
      const venueOK = v === "ALL" || p.venue === v;
      const searchOK = !s || p.pair.toUpperCase().includes(s);
      return quoteOK && venueOK && searchOK;
    });
  }, [pairs, q, v, search]);

  return (
    <div className="pairs-page" style={{ maxWidth: 1440, margin: "0 auto", padding: "56px 32px 40px" }}>
      <h1 className="serif" style={{ fontSize: 56, letterSpacing: "-0.02em", margin: "0 0 24px" }} data-testid="pairs-title">
        Pairs.
      </h1>

      <div className="pairs-search-row" style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 20 }}>
        <span style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--ink-muted)" }}>Search</span>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Ticker or pair (e.g. NVDA, WETH/USDG)"
          data-testid="pairs-search-input"
          spellCheck={false}
          autoComplete="off"
          style={{
            flex: 1,
            maxWidth: 420,
            background: "transparent",
            border: "none",
            borderBottom: "1px solid var(--line)",
            color: "var(--ink)",
            fontSize: 14,
            padding: "8px 2px",
            outline: "none",
            fontFamily: "inherit",
            letterSpacing: "0.01em",
          }}
        />
        {search && (
          <button
            onClick={() => setSearch("")}
            className="btn-ghost"
            data-testid="pairs-search-clear"
            style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--ink-muted)" }}
          >
            Clear
          </button>
        )}
        <span className="tabular" style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--ink-muted)" }}>
          {rows.length} {rows.length === 1 ? "pair" : "pairs"}
        </span>
      </div>

      <div className="pairs-filter-row">
        <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--ink-muted)" }}>Quote</span>
          {QUOTE_FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setQ(f)}
              className="btn-ghost"
              style={{ opacity: q === f ? 1 : 0.4 }}
              data-testid={`filter-quote-${f.toLowerCase()}`}
            >
              {f === "ALL" ? "All" : f}
            </button>
          ))}
        </div>
        <div className="pairs-filter-spacer" style={{ flex: 1 }} />
        <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--ink-muted)" }}>Venue</span>
          {VENUE_FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setV(f)}
              className="btn-ghost"
              style={{ opacity: v === f ? 1 : 0.4 }}
              data-testid={`filter-venue-${f}`}
            >
              {f === "ALL" ? "All" : f}
            </button>
          ))}
        </div>
      </div>

      <table className="book tabular" data-testid="pairs-table">
        <thead>
          <tr><th>Pair</th><th>Venue</th><th>Last</th><th>Liquidity</th><th>24h Vol</th><th>Δ</th><th></th></tr>
        </thead>
        <tbody>
          {rows.map((p) => {
            const base = p.pair.split("/")[0];
            const listed = isListedTicker(base);
            return (
              <tr key={p.pair}>
                <td style={{ fontSize: 15 }}>{p.pair}</td>
                <td style={{ color: "var(--ink-muted)" }}>Uniswap {p.venue}</td>
                <td>{p.last == null ? "—" : Number(p.last).toFixed(2)}</td>
                <td>{p.liq}</td>
                <td>{p.vol24}</td>
                <td><DeltaText d={p.delta} /></td>
                <td>
                  {listed ? (
                    <Link
                      to={`/listed/${base.toLowerCase()}`}
                      className="btn-ghost"
                      data-testid={`pairs-open-${p.pair}`}
                      style={{ textDecoration: "none" }}
                    >
                      Open
                    </Link>
                  ) : p.pool ? (
                    <a
                      href={`https://robinhoodchain.blockscout.com/address/${p.pool}`}
                      target="_blank"
                      rel="noreferrer"
                      className="btn-ghost"
                      data-testid={`pairs-open-${p.pair}`}
                      style={{ textDecoration: "none" }}
                    >
                      Pool
                    </a>
                  ) : (
                    <span style={{ color: "var(--ink-muted)" }}>—</span>
                  )}
                </td>
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr>
              <td colSpan={7} style={{ color: "var(--ink-muted)", padding: 40 }}>
                {search ? (
                  <div style={{ fontSize: 13 }}>
                    No pairs match “{search}”. <button onClick={() => setSearch("")} className="btn-ghost" style={{ fontSize: 12.5 }}>Clear search</button>
                  </div>
                ) : q === "ETH" ? (
                  <>
                    <div style={{ fontSize: 14, color: "var(--ink)", marginBottom: 6 }}>No ETH-quoted pairs in the tracked book yet.</div>
                    <div style={{ fontSize: 12.5 }}>
                      Wrap ETH → WETH · WETH is <span className="tabular">0x0Bd7…D73</span> · fresh WETH pairs stream into <Link to="/new" style={{ color: "var(--ink-muted)" }}>New</Link>.
                    </div>
                  </>
                ) : q === "NVDA" ? (
                  <div style={{ fontSize: 13 }}>
                    No pairs quoted in NVDA yet. When a project chooses NVDA as the quote (like GOLDLEAF/NVDA), it appears here.
                  </div>
                ) : q === "SPY" ? (
                  <>
                    <div style={{ fontSize: 14, color: "var(--ink)", marginBottom: 6 }}>No SPY-quoted pairs in the tracked book yet.</div>
                    <div style={{ fontSize: 12.5 }}>
                      SPY is a listed index token · view its dual pricing on <Link to="/listed/spy" style={{ color: "var(--ink-muted)" }}>Listed</Link> · projects that pair against SPY as quote surface here.
                    </div>
                  </>
                ) : (
                  <div style={{ textAlign: "center" }}>Nothing here.</div>
                )}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
